import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { GRID_SPACING_DEFAULT } from './constants';
import { useApplicationContext } from '@/context/v1/Application/context';
import {
	getHierarchyEndDateOrToday,
	getHierarchyStartDateOrDefault
} from '@/components/Settings/DataExtraction/utils/hierarchyEndDate';
import type { Context } from './types';

interface DataExtractionProviderProps {
	children: JSX.Element | JSX.Element[];
}

const DataExtractionReportContext = createContext<Context>({} as Context);

export function DataExtractionReportProvider({ children }: Readonly<DataExtractionProviderProps>) {
	const { id } = useParams<{ id: string }>();
	const { organization, company } = useApplicationContext();

	const [currentStep, setCurrentStep] = useState<number>(0);
	const [dataExtractionReport, setDataExtractionReport] = useState<any>();
	const [extractActionPlans, setExtractActionPlans] = useState<boolean>(false);
	const [actionplansCount, setActionplansCount] = useState<number>(0);
	const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
	const [selectedReportNames, setSelectedReportNames] = useState<string[]>([]);
	const [reportTypeSelectedCount, setReportTypeSelectedCount] = useState<number>(0);
	const [isSelectAllMode, setIsSelectAllMode] = useState<boolean>(false);
	const [selectedReportsCount, setSelectedReportsCount] = useState<number>(0);

	const isEditing = !!id;

	const parameters = useMemo(
		() => ({
			organization_id: organization?.id,
			company_id: company?.id,
			id: id!
		}),
		[organization?.id, company?.id, id]
	);

	const handleUpdateCurrentStep = useCallback((step: number): void => {
		if (step < 0) {
			return;
		}

		setCurrentStep(step);
	}, []);

	const handleNextStep = useCallback((): void => {
		setCurrentStep((prev) => prev + 1);
	}, []);

	const handleOnPreviousStep = useCallback((): void => {
		setCurrentStep((prev) => Math.max(0, prev - 1));
	}, []);

	const handleSetDataExtractionReport = useCallback((data: any): void => {
		if (!data) {
			setDataExtractionReport(undefined);
			return;
		}

		setDataExtractionReport({
			...data,
			start_date: getHierarchyStartDateOrDefault(data.start_date),
			end_date: getHierarchyEndDateOrToday(data.end_date)
		});
	}, []);

	const handleSetExtractActionPlans = useCallback((value: boolean): void => {
		setExtractActionPlans(value);
	}, []);

	const handleSetActionplansCount = useCallback((count: number): void => {
		setActionplansCount(count);
	}, []);

	const handleSetShowConfirmationModal = useCallback((show: boolean): void => {
		setShowConfirmationModal(show);
	}, []);

	const handleSetSelectedReportNames = useCallback((names: string[]) => {
		setSelectedReportNames(names);
	}, []);

	const handleSetReportTypeSelectedCount = useCallback((count: number): void => {
		setReportTypeSelectedCount(count);
	}, []);

	const handleSetIsSelectAllMode = useCallback((isSelectAll: boolean): void => {
		setIsSelectAllMode(isSelectAll);
	}, []);

	const handleSetSelectedReportsCount = useCallback((count: number): void => {
		setSelectedReportsCount(count);
	}, []);

	const contextValue = useMemo(
		(): Context => ({
			parameters,
			currentStep,
			gutterLayout: GRID_SPACING_DEFAULT,
			dataExtractionReport,
			isEditing,
			extractActionPlans,
			actionplansCount,
			showConfirmationModal,
			selectedReportNames,
			reportTypeSelectedCount,
			isSelectAllMode,
			selectedReportsCount,
			onNextStep: handleNextStep,
			onPreviousStep: handleOnPreviousStep,
			onChangeStep: handleUpdateCurrentStep,
			onSetDataExtractionReport: handleSetDataExtractionReport,
			onSetExtractActionPlans: handleSetExtractActionPlans,
			onSetActionplansCount: handleSetActionplansCount,
			onSetShowConfirmationModal: handleSetShowConfirmationModal,
			onSetSelectedReportNames: handleSetSelectedReportNames,
			onSetReportTypeSelectedCount: handleSetReportTypeSelectedCount,
			onSetIsSelectAllMode: handleSetIsSelectAllMode,
			onSetSelectedReportsCount: handleSetSelectedReportsCount
		}),
		[
			parameters,
			currentStep,
			dataExtractionReport,
			isEditing,
			extractActionPlans,
			actionplansCount,
			showConfirmationModal,
			selectedReportNames,
			reportTypeSelectedCount,
			isSelectAllMode,
			selectedReportsCount,
			handleNextStep,
			handleOnPreviousStep,
			handleUpdateCurrentStep,
			handleSetDataExtractionReport,
			handleSetExtractActionPlans,
			handleSetActionplansCount,
			handleSetShowConfirmationModal,
			handleSetSelectedReportNames,
			handleSetReportTypeSelectedCount,
			handleSetIsSelectAllMode,
			handleSetSelectedReportsCount
		]
	);

	return <DataExtractionReportContext.Provider value={contextValue}>{children}</DataExtractionReportContext.Provider>;
}

export function useDataExtractionReport(): Context {
	const context = useContext(DataExtractionReportContext);

	if (!context) {
		throw new Error('useDataExtractionReport must be used within a DataExtractionReportProvider');
	}

	return context;
}
